import { set } from 'lodash-es'
import type { Dispatch, SetStateAction } from 'react'
import { useMemo, useState } from 'react'
import { clonePlainObjectDeep } from '.'
import type { FlattenPath } from './type'

export interface HandleChange<
  T = any,
  K = T extends any ? string : keyof FlattenPath<Required<T>>,
> {
  (field: K, embedded: true): HandleChange<any>
  (field?: K): (e: any) => void
}

const getValue = (e: any) => e?.target?.value ?? e

export const createHandleChange = <T = any>(
  basePath: string[] = [],
  setValues: Dispatch<SetStateAction<any>> = () => {},
): HandleChange<T> =>
  ((field?: string, embedded?: boolean) =>
    field == null
      ? (e: any) =>
          setValues((v: any) =>
            set(clonePlainObjectDeep(v), basePath, getValue(e)),
          )
      : embedded
        ? createHandleChange([...basePath, field], setValues)
        : (e: any) =>
            setValues((v: any) =>
              set(
                clonePlainObjectDeep(v),
                [...basePath, field].join('.'),
                getValue(e),
              ),
            )) as HandleChange<T>

export const useForm = <T = any>(initialValues: T | (() => T) = {} as T) => {
  const [values, setValues] = useState(initialValues)

  const handleChange = useMemo(() => createHandleChange<T>([], setValues), [])

  const getValues = useMemo(
    () => () =>
      new Promise<T>(resolve => {
        setValues(values => {
          resolve(values)
          return values
        })
      }),
    [],
  )

  return useMemo(() => {
    return {
      values,
      setValues,
      handleChange,
      getValues,
    } as const
  }, [values])
}
